import React, { Component } from "react";
import axios from "axios";
import { connect } from "react-redux";
import { Spin, Empty, Tag, Modal, message } from "antd";
import KanbanTablero from "../../../Components/KanbanTablero";
import { fmtFechaDia } from "../../../Components/Items";
import VerAgrupacion from "../Agrupaciones/VerAgrupacion/VerAgrupacion";
import EditarAgrupacion from "../Agrupaciones/EditarAgrupacion/EditarAgrupacion";
import ModalAsignar from "./ModalAsignar";
import ModalTerminar from "./ModalTerminar";
import ModalDetalleAsignacion from "./ModalDetalleAsignacion";
import { COLOR_ESTATUS } from "./HistorialTrabajo";

// Separador del id de columna: "<etapaId>|<empleadoId>", y "<etapaId>|" para "Por asignar".
const SEP = "|";
const idColumna = (etapaId, empleadoId) => `${etapaId}${SEP}${empleadoId || ""}`;
const leerColumna = (id) => {
  const [etapaId, empleadoId] = String(id).split(SEP);
  return { etapaId, empleadoId: empleadoId || null };
};

// Tablero "Por persona" del Dashboard de Taxidermia: una seccion por etapa con control por
// persona (Montaje, Retoque...) y en cada una la columna "Por asignar" + una por persona.
// Cada seccion es su propio KanbanTablero, asi que una pieza no se puede arrastrar a otra
// etapa (eso se hace con "Terminar" o en la pestaña "Por etapa").
class TableroPersonal extends Component {
  state = {
    loading: false,
    etapas: [],
    cargado: false,
    asignar: null, // { agrupacionIds, descripcion, etapa, empleadoIdInicial }
    detalle: null, // { tarjeta, etapa }
    terminar: null, // { tarjeta, etapa }
    editarAgrupacion: null, // { agrupacionId, ediciones, payload }
    verAgrupacionId: null,
  };

  componentDidMount() {
    this.consultar();
  }

  consultar = () => {
    this.setState({ loading: true });
    axios
      .post(
        "asignacionesAgrupacion/selectTableroPersonal",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({ loading: false, cargado: true, etapas: res.data.etapas || [] });
        } else {
          this.setState({ loading: false, cargado: true, etapas: [] });
          message.error(res.data.message || "Error al cargar el tablero");
        }
      })
      .catch((err) => {
        console.error(err);
        this.setState({ loading: false, cargado: true, etapas: [] });
        message.error("Error al cargar el tablero");
      });
  };

  // ─── Datos ──────────────────────────────────────────────────────────────

  buscarTarjeta(etapa, agrupacionId) {
    const id = String(agrupacionId);
    const enPorAsignar = etapa.porAsignar.find((t) => String(t._id) === id);
    if (enPorAsignar) return { tarjeta: enPorAsignar, persona: null };
    for (const persona of etapa.personas) {
      const tarjeta = persona.tarjetas.find((t) => String(t._id) === id);
      if (tarjeta) return { tarjeta: { ...tarjeta, personaNombre: persona.nombre }, persona };
    }
    return { tarjeta: null, persona: null };
  }

  // Para los selectores de persona (asignar, reasignar): todas las columnas de la etapa, que
  // incluyen a quien tiene piezas aunque ya no este en el personal configurado.
  etapaParaModal(etapa) {
    return { ...etapa, personal: etapa.personas.map((p) => ({ _id: p.empleadoId, nombre: p.nombre })) };
  }

  etiquetaPieza(tarjeta) {
    return `${tarjeta.numero ? "#" + tarjeta.numero + " · " : ""}${tarjeta.especie || "Pieza"}`;
  }

  tarjetaKanban = (tarjeta) => ({
    ...tarjeta,
    _id: String(tarjeta._id),
    nombre: this.etiquetaPieza(tarjeta),
    descripcion: [
      tarjeta.clienteNombre,
      tarjeta.detalle,
      tarjeta.fechaEntrega ? "Entrega " + fmtFechaDia(tarjeta.fechaEntrega) : "",
    ]
      .filter(Boolean)
      .join(" · "),
    textoTag: null,
  });

  columnas(etapa) {
    return [
      {
        _id: idColumna(etapa._id),
        nombre: "Por asignar",
        actividades: etapa.porAsignar.map(this.tarjetaKanban),
      },
      ...etapa.personas.map((persona) => ({
        _id: idColumna(etapa._id, persona.empleadoId),
        // Con asterisco quien tiene piezas pero ya no esta en el personal de la etapa.
        nombre: persona.nombre + (persona.enPersonal ? "" : " *"),
        actividades: persona.tarjetas.map(this.tarjetaKanban),
      })),
    ];
  }

  renderExtraTarjeta = (item) => {
    if (!item.asignacionId) {
      return item.horarioExtra ? (
        <Tag color="purple" style={{ marginTop: 6 }}>
          Horario extra
        </Tag>
      ) : null;
    }
    return (
      <div style={{ marginTop: 6, fontSize: 12, display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
        <Tag color={COLOR_ESTATUS[item.estatusTexto] || "default"}>{item.estatusTexto}</Tag>
        <span style={{ color: "#555" }}>
          Desde {fmtFechaDia(item.fechaInicio)} · {item.diasHabiles} día{item.diasHabiles === 1 ? "" : "s"} háb.
        </span>
        {item.horarioExtra && <Tag color="purple">HE</Tag>}
      </div>
    );
  };

  // ─── Arrastrar ──────────────────────────────────────────────────────────

  moverTarjeta = (etapa, agrupacionId, columnaDestinoId) => {
    const destino = leerColumna(columnaDestinoId);
    const { tarjeta, persona } = this.buscarTarjeta(etapa, agrupacionId);
    if (!tarjeta) return;
    const empleadoOrigen = persona ? String(persona.empleadoId) : null;
    if (empleadoOrigen === destino.empleadoId) return;

    // Por asignar -> persona: asignar.
    if (!persona && destino.empleadoId) {
      this.setState({
        asignar: {
          agrupacionIds: [tarjeta._id],
          descripcion: this.etiquetaPieza(tarjeta),
          etapa: this.etapaParaModal(etapa),
          empleadoIdInicial: destino.empleadoId,
        },
      });
      return;
    }

    // Persona -> Por asignar: quitar la asignacion.
    if (persona && !destino.empleadoId) {
      Modal.confirm({
        title: `¿Quitar la asignación de ${this.etiquetaPieza(tarjeta)}?`,
        content: "La pieza regresa a 'Por asignar' y no sale en el reporte.",
        okText: "Quitar",
        cancelText: "Cancelar",
        onOk: () => this.llamar("cancelarAsignacion", { asignacionId: tarjeta.asignacionId }, "Asignación quitada"),
      });
      return;
    }

    // Persona -> otra persona: reasignar a partir de hoy.
    const nuevaPersona = etapa.personas.find((p) => String(p.empleadoId) === destino.empleadoId);
    Modal.confirm({
      title: `¿Pasar ${this.etiquetaPieza(tarjeta)} a ${nuevaPersona?.nombre || "otra persona"}?`,
      content:
        "A partir de hoy. Para elegir otra fecha usa 'Reasignar' en el detalle de la pieza.",
      okText: "Reasignar",
      cancelText: "Cancelar",
      onOk: () =>
        this.llamar(
          "reasignarAsignacion",
          { asignacionId: tarjeta.asignacionId, empleadoId: destino.empleadoId },
          "Pieza reasignada"
        ),
    });
  };

  moverVarias = (etapa, agrupacionIds, columnaDestinoId) => {
    const destino = leerColumna(columnaDestinoId);
    const tarjetas = agrupacionIds.map((id) => this.buscarTarjeta(etapa, id));
    if (!destino.empleadoId || tarjetas.some((t) => !t.tarjeta || t.persona)) {
      message.info("Para asignar varias piezas juntas, selecciónalas en 'Por asignar' y arrástralas a una persona");
      return;
    }
    this.setState({
      asignar: {
        agrupacionIds: tarjetas.map((t) => t.tarjeta._id),
        descripcion: tarjetas.map((t) => this.etiquetaPieza(t.tarjeta)).join(", "),
        etapa: this.etapaParaModal(etapa),
        empleadoIdInicial: destino.empleadoId,
      },
    });
  };

  clicTarjeta = (etapa, agrupacionId) => {
    const { tarjeta } = this.buscarTarjeta(etapa, agrupacionId);
    if (!tarjeta) return;
    if (tarjeta.asignacionId) {
      this.setState({ detalle: { tarjeta, etapa: this.etapaParaModal(etapa) } });
    } else {
      this.setState({
        asignar: {
          agrupacionIds: [tarjeta._id],
          descripcion: this.etiquetaPieza(tarjeta),
          etapa: this.etapaParaModal(etapa),
          empleadoIdInicial: null,
        },
      });
    }
  };

  llamar = (ruta, datos, exito) => {
    this.setState({ loading: true });
    return axios
      .post(`asignacionesAgrupacion/${ruta}`, { empresaId: this.props.empresaId, ...datos }, { headers: this.props.headersToken })
      .then((res) => {
        if (res.data.success === 2) {
          message.success(exito);
        } else {
          message.warning(res.data.message || "No se pudo guardar");
        }
        this.consultar();
      })
      .catch((err) => {
        console.error(err);
        this.setState({ loading: false });
        message.error("Error al conectar con el servidor");
      });
  };

  // ─── Modales ────────────────────────────────────────────────────────────

  cerrarAsignar = (asignado) => {
    this.setState({ asignar: null });
    if (asignado) this.consultar();
  };

  cerrarDetalle = (huboCambios) => {
    this.setState({ detalle: null });
    if (huboCambios) this.consultar();
  };

  abrirTerminar = (tarjeta) => {
    const etapa = this.state.detalle?.etapa;
    this.setState({ detalle: null, terminar: { tarjeta, etapa } });
  };

  cerrarTerminar = (hecho) => {
    this.setState({ terminar: null });
    if (hecho) this.consultar();
  };

  // La etapa destino tiene "Editar al entrar": primero se edita la agrupacion y despues se
  // repite el mismo terminar con omitirEditarAlEntrar (igual que el Kanban por etapa).
  requiereEditar = ({ payload, etapa }) => {
    this.setState({
      terminar: null,
      editarAgrupacion: { agrupacionId: this.state.terminar?.tarjeta?.agrupacionId, ediciones: etapa, payload },
    });
  };

  cerrarEditarAgrupacion = (e) => {
    const pendiente = this.state.editarAgrupacion;
    this.setState({ editarAgrupacion: null });
    if (!e?.cerrarModificado || !pendiente) {
      message.info("La pieza no se terminó");
      return;
    }
    this.llamar(
      "terminarAsignacion",
      { ...pendiente.payload, omitirEditarAlEntrar: true },
      "Pieza terminada y enviada a la siguiente etapa"
    );
  };

  // ─── Render ─────────────────────────────────────────────────────────────

  renderEtapa(etapa) {
    const enProceso = etapa.personas.reduce((s, p) => s + p.tarjetas.length, 0);
    return (
      <div key={etapa._id} style={{ marginBottom: 18 }}>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 12,
            borderBottom: "2px solid #00BFD6",
            paddingBottom: 4,
          }}
        >
          <span style={{ fontSize: 16, fontWeight: "bold", color: "#005F6B", textTransform: "uppercase" }}>
            {etapa.nombre}
          </span>
          <span style={{ color: "#666", fontSize: 13 }}>
            {etapa.porAsignar.length} por asignar · {enProceso} asignada{enProceso === 1 ? "" : "s"}
          </span>
        </div>
        <KanbanTablero
          etapas={this.columnas(etapa)}
          onActividadMove={(id, columna) => this.moverTarjeta(etapa, id, columna)}
          onActividadMoveMultiple={(ids, columna) => this.moverVarias(etapa, ids, columna)}
          onActividadClick={(id) => this.clicTarjeta(etapa, id)}
          renderExtra={this.renderExtraTarjeta}
          anchoColumna={260}
          disableExcel
          disableVer
        />
      </div>
    );
  }

  render() {
    const { etapas, cargado } = this.state;
    return (
      <Spin spinning={this.state.loading}>
        {cargado && etapas.length === 0 ? (
          <Empty
            style={{ padding: 40 }}
            description={
              <span>
                Ninguna etapa lleva control por persona.
                <br />
                En <b>Taxidermia → Etapas</b>, edita la etapa (por ejemplo Montaje y Retoque), activa
                <b> "Controlar trabajo por persona"</b> y elige su personal.
              </span>
            }
          />
        ) : (
          etapas.map((etapa) => this.renderEtapa(etapa))
        )}

        <ModalAsignar
          visible={!!this.state.asignar}
          agrupacionIds={this.state.asignar?.agrupacionIds || []}
          descripcion={this.state.asignar?.descripcion}
          etapa={this.state.asignar?.etapa}
          empleadoIdInicial={this.state.asignar?.empleadoIdInicial}
          onCerrar={this.cerrarAsignar}
        />
        <ModalDetalleAsignacion
          visible={!!this.state.detalle}
          tarjeta={this.state.detalle?.tarjeta}
          etapa={this.state.detalle?.etapa}
          onCerrar={this.cerrarDetalle}
          onTerminar={this.abrirTerminar}
          onVerAgrupacion={(id) => this.setState({ verAgrupacionId: id })}
        />
        <ModalTerminar
          visible={!!this.state.terminar}
          tarjeta={this.state.terminar?.tarjeta}
          etapa={this.state.terminar?.etapa}
          onCerrar={this.cerrarTerminar}
          onRequiereEditar={this.requiereEditar}
        />
        <EditarAgrupacion
          visible={!!this.state.editarAgrupacion}
          cerrarEditarAgrupacion={this.cerrarEditarAgrupacion}
          agrupacionId={this.state.editarAgrupacion?.agrupacionId}
          ediciones={this.state.editarAgrupacion?.ediciones}
          cancelarEdiciones
          headersToken={this.props.headersToken}
          empresaId={this.props.empresaId}
        />
        <VerAgrupacion
          visible={!!this.state.verAgrupacionId}
          cerrarVerAgrupacion={() => this.setState({ verAgrupacionId: null })}
          agrupacionId={this.state.verAgrupacionId}
          headersToken={this.props.headersToken}
          empresaId={this.props.empresaId}
        />
      </Spin>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(TableroPersonal);
